import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import { type Attachment, type Mention, type Poll } from "@/schemas";

import type { EngagementSnapshot } from "@/hooks/useEngagementCapture";

type IComposerContext = {
  composers: FormValue[];
  setComposers: React.Dispatch<React.SetStateAction<FormValue[]>>;
};

const ComposerContext = createContext<any>({});

interface ComposerProviderProps {
  children: React.ReactNode;
}

enum SportType {
  NBA = "nba",
  MLB = "mlb",
  CBB = "cbb",
  SOCCER = "soccer",
}

const SHARABLE_SPORT_TYPES = Object.values(SportType);

type ComposeEmbed =
  | { type: "status"; statusId: string }
  | { type: "prediction"; statusId: string }
  | { type: "news"; newsArticleId: string }
  | { type: "game"; sportType: SportType; sportGameId: string };

interface FormValue {
  composeId: string;
  text: string;
  poll: Poll | undefined;
  media: Attachment[];
  inReplyToStatusId: string;
  inReplyTo: Mention[];
  groupId: string;
  embed: ComposeEmbed | null;
  groupTimelineVisible: boolean;
  isSubmitting: boolean;
  isUploading: boolean;
  suggestions: any[];
  title: string;
  uploadingCount: number;
  placeholderId: string;
  scheduledAt: Date | undefined;
  statusId?: string;
  published: boolean;
  isPredictions: boolean;
  predictionId: string;
  boostEnabled: boolean;
  engagementContext: EngagementSnapshot | undefined;
}

const initialFormValue = {
  composeId: "",
  text: "",
  poll: undefined,
  media: [],
  inReplyToStatusId: "",
  inReplyTo: [],
  groupId: "",
  embed: null,
  groupTimelineVisible: false,
  isSubmitting: false,
  isUploading: false,
  suggestions: [],
  title: "",
  uploadingCount: 0,
  placeholderId: "",
  scheduledAt: undefined,
  published: true,
  isPredictions: false,
  predictionId: "",
  boostEnabled: false,
  engagementContext: undefined,
};

const ComposerProvider: React.FC<ComposerProviderProps> = ({ children }) => {
  const [composers, setComposers] = useState<FormValue[]>([]);

  const value = useMemo(
    () => ({
      composers,
      setComposers,
    }),
    [composers],
  );

  return (
    <ComposerContext.Provider value={value}>
      {children}
    </ComposerContext.Provider>
  );
};

const useComposerContext = (): IComposerContext => useContext(ComposerContext);

function useComposer(composeId: string): {
  composer: FormValue;
  updateComposer: (nextValues: Partial<FormValue>) => void;
  clearComposer: () => void;
} {
  const { composers, setComposers } = useComposerContext();

  const location = useLocation();
  const isOnGroupPage = location.pathname.startsWith("/group/");

  const composer =
    composers?.find((composer) => composer.composeId === composeId) ||
    initialFormValue;

  // Stabilize callback identities so effects that depend on `updateComposer` /
  // `clearComposer` don't re-run every render. The state updater form of
  // setComposers keeps the reference free of the live `composers` array.
  const updateComposer = useCallback(
    (nextValues: Partial<FormValue>) => {
      setComposers((prevComposers) => {
        if (
          !prevComposers.some((composer) => composer.composeId === composeId)
        ) {
          return [
            ...prevComposers,
            { ...initialFormValue, ...nextValues, composeId },
          ];
        }

        return prevComposers.map((composer) => {
          if (composer.composeId === composeId) {
            return {
              ...composer,
              ...nextValues,
            };
          }

          return composer;
        });
      });
    },
    [composeId, setComposers],
  );

  const clearComposer = useCallback(() => {
    setComposers((prevComposers) =>
      prevComposers.map((composer) =>
        composer.composeId === composeId
          ? {
              ...initialFormValue,
              composeId,
              // Preserve groupId if on group page
              ...(isOnGroupPage && { groupId: composer.groupId }),
            }
          : composer,
      ),
    );
  }, [composeId, isOnGroupPage, setComposers]);

  return { composer, updateComposer, clearComposer };
}

export {
  ComposerProvider,
  useComposer,
  type IComposerContext,
  initialFormValue as composerInitialFormValue,
  type FormValue as ComposeFormValue,
  type ComposeEmbed,
  SportType,
  SHARABLE_SPORT_TYPES,
};
